import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, Plus, LogOut, RefreshCw, Users, UserPlus,
  Clock3, ShieldCheck, MapPin, BriefcaseBusiness,
  TrendingUp, ChevronRight, LockKeyhole, FileCheck,
  LayoutDashboard, Menu, X
} from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Footer from "@/components/Footer";
import FileUpload from "@/components/FileUpload";
import { BRAND_LOGO } from "@/lib/constants";

const futureModules = [
  { title: "CRM & Leads", desc: "Manage customer enquiries and follow-ups.", icon: BriefcaseBusiness },
  { title: "Targets & Performance", desc: "Track employee goals and achievements.", icon: TrendingUp },
  { title: "Attendance & Work Hours", desc: "Attendance and working-hour records.", icon: Clock3 },
  { title: "Leave Management", desc: "Employee leave requests and balances.", icon: CalendarIcon },
  { title: "GPS Field Visits", desc: "Field activity and visit tracking.", icon: MapPin },
  { title: "Expenses & Allowances", desc: "Manage field expenses and claims.", icon: WalletIcon },
];

function CalendarIcon(props) {
  return <Clock3 {...props} />;
}
function WalletIcon(props) {
  return <BriefcaseBusiness {...props} />;
}

const initials = (name = "Employee") =>
  name.split(" ").filter(Boolean).slice(0, 2).map(x => x[0]).join("").toUpperCase();

function KpiCard({ title, value, subtitle, icon: Icon, gradient }) {
  return (
    <div className={`group relative overflow-hidden rounded-2xl border p-5 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-lg ${
      gradient.includes("blue") ? "border-blue-200 bg-gradient-to-br from-blue-50 via-white to-indigo-100/80" :
      gradient.includes("violet") ? "border-purple-200 bg-gradient-to-br from-violet-50 via-white to-fuchsia-100/80" :
      gradient.includes("amber") ? "border-amber-200 bg-gradient-to-br from-amber-50 via-white to-orange-100/80" :
      "border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-teal-100/80"
    }`}>
      <div className={`mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br ${gradient} text-white shadow-md`}>
        <Icon size={21} />
      </div>
      <p className="text-sm font-medium text-slate-500">{title}</p>
      <p className="mt-1 text-3xl font-extrabold tracking-tight text-slate-900 tabular-nums">{value}</p>
      <p className="mt-2 text-xs text-slate-400">{subtitle}</p>
      <div className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-violet-100/50 blur-2xl transition-all group-hover:bg-fuchsia-100/70" />
    </div>
  );
}

export default function EmployeeDashboard() {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const [employee, setEmployee] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [vendorStats, setVendorStats] = useState({
    mine: 0, drafts: 0, submitted: 0, verified: 0
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const pageSize = 25;
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [profileBusy, setProfileBusy] = useState(true);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [attendanceToday, setAttendanceToday] = useState(null);
  const [attendanceSummary, setAttendanceSummary] = useState(null);
  const [attendanceBusy, setAttendanceBusy] = useState(false);


  const permissions = employee?.permissions || {};
  const canAdd = permissions.add_vendors !== false;
  const canView = permissions.view_vendors !== false;

  const search = useCallback(async (q = query, nextPage = 1) => {
    if (!canView) return;
    setBusy(true);
    try {
      const { data } = await api.get("/employee/vendors", {
        params: { q, page: nextPage, page_size: pageSize }
      });
      setVendors(data.vendors || []);
      setTotalCount(Number(data.count ?? 0));
      setVendorStats({
        mine: Number(data.stats?.mine ?? 0),
        drafts: Number(data.stats?.drafts ?? 0),
        submitted: Number(data.stats?.submitted ?? 0),
        verified: Number(data.stats?.verified ?? 0),
      });
      setCurrentPage(Number(data.page ?? nextPage));
      setTotalPages(Number(data.total_pages ?? 0));
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not load vendor records");
    } finally {
      setBusy(false);
    }
  }, [query, canView]);

  useEffect(() => {
    if (!loading && user?.role !== "employee") navigate("/employee/login");
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!loading && user?.role === "employee") {
      api.get("/employee/auth/me")
        .then(({ data }) => setEmployee(data))
        .catch(() => toast.error("Could not load employee profile"))
        .finally(() => setProfileBusy(false));
    }
  }, [loading, user]);

  useEffect(() => {
    if (employee && canView) search("");
  }, [employee, canView, search]);

  // Employees can view their own aggregate KPIs without vendor-list permission.
  const loadKpis = useCallback(async () => {
    if (!employee) return;
    try {
      const { data } = await api.get("/employee/vendors/kpis");
      setVendorStats({
        mine: Number(data.stats?.mine ?? 0),
        drafts: Number(data.stats?.drafts ?? 0),
        submitted: Number(data.stats?.submitted ?? 0),
        verified: Number(data.stats?.verified ?? 0),
      });
    } catch {
      toast.error("Could not load your vendor performance totals");
    }
  }, [employee]);


  const loadAttendance = useCallback(async () => {
    if (!employee) return;
    setAttendanceBusy(true);
    try {
      const [todayResponse, summaryResponse] = await Promise.all([
        api.get("/employee/attendance/today"),
        api.get("/employee/attendance/summary"),
      ]);
      setAttendanceToday(todayResponse.data?.attendance || null);
      setAttendanceSummary(summaryResponse.data || null);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not load attendance");
    } finally {
      setAttendanceBusy(false);
    }
  }, [employee]);

  useEffect(() => {
    loadKpis();
  }, [loadKpis]);

  useEffect(() => {
    loadAttendance();
  }, [loadAttendance]);

  const refreshDashboard = async () => {
    setRefreshing(true);
    try {
      await Promise.all([loadKpis(), loadAttendance()]);
      if (canView) {
        await search(query, currentPage);
      }
    } finally {
      setRefreshing(false);
    }
  };

  const signOut = async () => {
    await logout();
    navigate("/employee/login");
  };

  if (loading || user?.role !== "employee") {
    return <div className="grid min-h-screen place-items-center bg-slate-50 text-slate-500">Loading employee portal…</div>;
  }

  const name = employee?.name || user?.name || "Employee";
  const designation = employee?.designation || "Employee";
  const territory = employee?.territory || "Territory not assigned";

  const handleProfilePhotoChange = async (files) => {
    const fileId = files[0] || null;
    try {
      const { data } = await api.put("/employee/auth/profile-image", {
        file_id: fileId,
      });
      setEmployee((prev) => ({
        ...prev,
        profile_image: data.profile_image,
      }));
      toast.success(fileId ? "Profile photo updated" : "Profile photo removed");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not update profile photo");
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f6fc] text-slate-900">
      <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-white/90 shadow-sm backdrop-blur-xl">
        <div className="mx-auto flex h-[76px] max-w-[1500px] items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
          <div className="flex min-w-0 items-center gap-3">
            <img src={BRAND_LOGO} alt="MakeMyEventPro" className="h-11 w-11 shrink-0 rounded-xl shadow-sm" />
            <div className="min-w-0">
              <div className="truncate text-base font-extrabold tracking-tight text-slate-900">
                MakeMy<span className="bg-gradient-to-r from-[#5e17eb] to-[#cc00ff] bg-clip-text text-transparent">Event</span>Pro
              </div>
              <p className="text-[11px] font-medium tracking-wide text-slate-500">EMPLOYEE WORKSPACE</p>
            </div>
          </div>

          <div className="hidden items-center gap-3 sm:flex">
            <div className="hidden text-right md:block">
              <p className="text-sm font-bold text-slate-800">{name}</p>
              <p className="text-xs text-slate-500">{designation}</p>
            </div>
            <div className="relative">
              <button
                type="button"
                onClick={() => setProfileMenuOpen(v => !v)}
                title="Change profile photo"
                aria-label="Change profile photo"
                className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-full border-2 border-purple-200 bg-gradient-to-br from-[#5e17eb] to-[#cc00ff] text-sm font-bold text-white shadow-md transition hover:ring-4 hover:ring-purple-100"
              >
                {employee?.profile_image
                  ? <img src={fileUrl(employee.profile_image)} alt={name} className="h-full w-full object-cover" />
                  : initials(name)}
              </button>
              {profileMenuOpen && (
                <div className="absolute right-0 top-full z-[70] mt-3 w-64 rounded-2xl border border-slate-200 bg-white p-4 shadow-xl">
                  <p className="mb-3 text-sm font-bold text-slate-800">Profile photo</p>
                  <FileUpload
                    value={employee?.profile_image ? [employee.profile_image] : []}
                    onChange={handleProfilePhotoChange}
                    kind="image"
                    purpose="profile"
                    max={1}
                    label="Change or remove photo"
                    accept="image/jpeg,image/png,image/webp"
                    testId="employee-profile-photo"
                  />
                  <button
                    type="button"
                    onClick={() => setProfileMenuOpen(false)}
                    className="mt-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
            <button onClick={signOut} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm font-semibold text-slate-600 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600">
              <LogOut size={16} /> <span className="hidden md:inline">Sign out</span>
            </button>
          </div>

          <button className="rounded-lg border p-2 text-slate-600 sm:hidden" onClick={() => setMobileMenu(v => !v)} aria-label="Toggle menu">
            {mobileMenu ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
        {mobileMenu && (
          <div className="border-t border-slate-100 bg-white px-4 py-4 sm:hidden">
            <div className="mb-3 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border-2 border-purple-200 bg-gradient-to-br from-[#5e17eb] to-[#cc00ff] font-bold text-white">
                {employee?.profile_image
                  ? <img src={fileUrl(employee.profile_image)} alt={name} className="h-full w-full object-cover" />
                  : initials(name)}
              </div>
              <div><p className="text-sm font-bold">{name}</p><p className="text-xs text-slate-500">{designation}</p></div>
            </div>
            <button onClick={signOut} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50"><LogOut size={16} /> Sign out</button>
          </div>
        )}
      </header>

      <main className="mx-auto max-w-[1500px] space-y-8 px-4 py-7 sm:px-6 sm:py-9 lg:px-8">
        <section className="relative overflow-hidden rounded-[26px] border border-white/80 bg-white/85 p-6 shadow-sm backdrop-blur-xl sm:p-8">
          <div className="pointer-events-none absolute -right-20 -top-28 h-72 w-72 rounded-full bg-purple-100/70 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-32 right-1/3 h-64 w-64 rounded-full bg-fuchsia-100/50 blur-3xl" />
          <div className="relative flex flex-col justify-between gap-6 lg:flex-row lg:items-center">
            <div>
              <div className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.2em] text-[#7c3aed]">
                <LayoutDashboard size={15} /> Employee Command Center
              </div>
              <h1 className="mt-3 text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
                Welcome,{" "}
                <span className="bg-gradient-to-r from-[#5e17eb] via-[#a21caf] to-[#cc00ff] bg-clip-text text-transparent">
                  {name}
                </span>
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
                Your workspace to manage vendor onboarding, monitor progress and grow the MakeMyEventPro network.
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-50 px-3 py-1.5 text-xs font-semibold text-violet-700"><BriefcaseBusiness size={13} /> {designation}</span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-fuchsia-50 px-3 py-1.5 text-xs font-semibold text-fuchsia-700"><MapPin size={13} /> {territory}</span>
              </div>
            </div>
            <div className="relative flex flex-wrap gap-2">
              <button onClick={refreshDashboard} disabled={refreshing} className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-violet-300 hover:text-violet-700 disabled:cursor-wait disabled:opacity-60">
                <RefreshCw size={16} className={refreshing || busy ? "animate-spin" : ""} /> {refreshing ? "Refreshing…" : "Refresh"}
              </button>
              {canAdd && <button onClick={() => navigate("/employee/vendors/new")} className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#5e17eb] to-[#cc00ff] px-5 py-3 text-sm font-bold text-white shadow-lg shadow-purple-200 transition hover:-translate-y-0.5 hover:shadow-xl">
                <Plus size={17} /> Add Vendor
              </button>}
            </div>
          </div>
        </section>

        <section>
          <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-violet-600">Performance overview</p>
              <h2 className="mt-1 text-xl font-extrabold tracking-tight text-slate-900">Vendor Activity</h2>
            </div>
            <p className="text-xs text-slate-400">Live totals across all matching vendor records</p>
          </div>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <KpiCard title="Onboarded by Me" value={vendorStats.mine} subtitle="Vendors created by you" icon={Users} gradient="from-blue-500 to-indigo-600" />
            <KpiCard title="My Draft Profiles" value={vendorStats.drafts} subtitle="Your incomplete vendor records" icon={Clock3} gradient="from-amber-400 to-orange-500" />
            <KpiCard title="My Submissions" value={vendorStats.submitted} subtitle="Submitted or under review" icon={UserPlus} gradient="from-violet-500 to-fuchsia-600" />
            <KpiCard title="My Verified Vendors" value={vendorStats.verified} subtitle="Approved or verified records" icon={ShieldCheck} gradient="from-emerald-500 to-teal-600" />
          </div>
        </section>


        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-violet-600">Employee self-service</p>
              <h2 className="mt-1 text-xl font-extrabold tracking-tight text-slate-900">My Attendance</h2>
              <p className="mt-1 text-sm text-slate-500">Today's attendance and your monthly working hours.</p>
            </div>
            <button
              type="button"
              onClick={loadAttendance}
              disabled={attendanceBusy}
              className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:border-violet-300 hover:text-violet-700 disabled:opacity-50"
            >
              {attendanceBusy ? "Loading..." : "Refresh attendance"}
            </button>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
              <p className="text-sm font-semibold text-slate-500">Today's status</p>
              <p className="mt-2 text-lg font-bold text-slate-900">
                {!attendanceToday
                  ? "Not checked in"
                  : attendanceToday.check_out
                    ? "Checked out"
                    : "Checked in"}
              </p>
              <div className="mt-3 space-y-2 text-sm text-slate-600">
                <p>
                  Check-in:{" "}
                  <strong className="text-slate-900">
                    {attendanceToday?.check_in?.at
                      ? new Date(attendanceToday.check_in.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                      : "—"}
                  </strong>
                </p>
                <p>
                  Check-out:{" "}
                  <strong className="text-slate-900">
                    {attendanceToday?.check_out?.at
                      ? new Date(attendanceToday.check_out.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                      : "—"}
                  </strong>
                </p>
                <p>
                  Verification:{" "}
                  <strong className="capitalize text-slate-900">
                    {attendanceToday?.status?.replaceAll("_", " ") || "Not recorded"}
                  </strong>
                </p>
                {attendanceToday?.working_hours && (
                  <p>
                    Working time: <strong className="text-slate-900">{attendanceToday.working_hours}</strong>
                  </p>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-violet-100 bg-violet-50/60 p-4">
              <p className="text-sm font-semibold text-violet-700">
                Monthly summary · {attendanceSummary?.month || "—"}
              </p>
              <p className="mt-2 text-2xl font-extrabold text-slate-900">
                {attendanceSummary?.total_working_hours || "0h 0m"}
              </p>
              <p className="text-xs text-slate-500">Completed working time</p>
              <div className="mt-4 grid grid-cols-3 gap-2">
                <div>
                  <p className="text-xl font-bold text-slate-900">{attendanceSummary?.total_days ?? "—"}</p>
                  <p className="text-xs text-slate-500">Days marked</p>
                </div>
                <div>
                  <p className="text-xl font-bold text-slate-900">{attendanceSummary?.completed_days ?? "—"}</p>
                  <p className="text-xs text-slate-500">Completed</p>
                </div>
                <div>
                  <p className="text-xl font-bold text-amber-600">{attendanceSummary?.pending_review ?? "—"}</p>
                  <p className="text-xs text-slate-500">In review</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section>
          <div className="mb-4">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-violet-600">Platform roadmap</p>
            <h2 className="mt-1 text-xl font-extrabold tracking-tight text-slate-900">Building What’s Next</h2>
            <p className="mt-1 text-sm text-slate-500">New capabilities are being developed to make your work easier.</p>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {futureModules.map(({ title, desc, icon: Icon }, index) => {
              const tones = [
                "border-blue-100 bg-gradient-to-br from-blue-50 to-white",
                "border-violet-100 bg-gradient-to-br from-violet-50 to-white",
                "border-amber-100 bg-gradient-to-br from-amber-50 to-white",
                "border-emerald-100 bg-gradient-to-br from-emerald-50 to-white",
                "border-fuchsia-100 bg-gradient-to-br from-fuchsia-50 to-white",
                "border-cyan-100 bg-gradient-to-br from-cyan-50 to-white"
              ];
              const iconTones = [
                "bg-blue-100 text-blue-700",
                "bg-violet-100 text-violet-700",
                "bg-amber-100 text-amber-700",
                "bg-emerald-100 text-emerald-700",
                "bg-fuchsia-100 text-fuchsia-700",
                "bg-cyan-100 text-cyan-700"
              ];
              return (
                <div key={title} className={`min-w-0 rounded-2xl border p-3 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md sm:p-4 ${tones[index % tones.length]}`}>
                  <div className={`mb-3 flex h-10 w-10 items-center justify-center rounded-xl ${iconTones[index % iconTones.length]}`}>
                    <Icon size={19} />
                  </div>
                  <h3 className="text-xs font-bold leading-5 text-slate-800 sm:text-sm">{title}</h3>
                  <p className="mt-1 text-[11px] leading-4 text-slate-500 sm:text-xs sm:leading-5">{desc}</p>
                </div>
              );
            })}
          </div>
        </section>

        <section className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
          <div className="flex flex-col justify-between gap-3 border-b border-slate-100 p-5 sm:flex-row sm:items-center">
            <div>
              <h2 className="text-lg font-extrabold text-slate-900">Vendor Management</h2>
              <p className="mt-1 text-xs text-slate-500">Search and access vendor onboarding records</p>
            </div>
            <form onSubmit={e => { e.preventDefault(); search(query, 1); }} className="flex w-full gap-2 sm:max-w-md">
              <div className="relative min-w-0 flex-1">
                <Search size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search vendor, phone or city" className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 pl-10 pr-3 text-sm outline-none transition focus:border-violet-400 focus:bg-white focus:ring-2 focus:ring-violet-100" />
              </div>
              <button className="rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white transition hover:bg-violet-700">Search</button>
            </form>
          </div>

          {!canView ? (
            <div className="p-8 text-center text-sm text-slate-500">You do not have permission to view vendors.</div>
          ) : vendors.length === 0 && !busy ? (
            <div className="p-10 text-center">
              <Users className="mx-auto mb-3 text-slate-300" size={34} />
              <p className="font-semibold text-slate-700">No vendor records found</p>
              <p className="mt-1 text-sm text-slate-400">Try another search or add a new vendor.</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {busy && <div className="px-5 py-3 text-xs text-violet-600">Updating vendor records…</div>}
              {vendors.map(v => {
                const own = v.created_by_employee_id === employee?.employee_id;
                const editable = !["submitted", "under_review", "approved", "verified"].includes(v.status) &&
                  (permissions.edit_all_vendors === true || (permissions.edit_own_vendors !== false && own));
                return (
                  <div key={v.id} className="flex flex-col gap-3 px-5 py-4 transition hover:bg-slate-50/80 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex min-w-0 items-start gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-violet-100 to-fuchsia-100 text-violet-700"><BriefcaseBusiness size={18} /></div>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-bold text-slate-800">{v.business_name || "Unnamed business"}</p>
                        <p className="mt-0.5 text-xs text-slate-500">{v.owner_name || "Owner not set"} · {v.business_phone || "No phone"}</p>
                        <p className="mt-1 flex items-center gap-1 text-xs text-slate-400"><MapPin size={12} /> {[v.city, v.state].filter(Boolean).join(", ") || "Location not set"}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-3 sm:justify-end">
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-bold capitalize text-slate-600">{(v.status || "draft").replaceAll("_", " ")}</span>
                      {own && <span className="text-[11px] font-semibold text-violet-600">Added by you</span>}
                      {editable && <button onClick={() => navigate(`/employee/vendors/${v.id}/onboarding`)} className="inline-flex items-center gap-1.5 rounded-lg border border-violet-200 px-3 py-2 text-xs font-bold text-violet-700 transition hover:bg-violet-50">Continue <ChevronRight size={14} /></button>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div className="flex flex-col gap-3 border-t border-slate-100 px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-slate-400">
              {busy ? "Loading…" : `${vendors.length} records on this page · ${totalCount} matching records`}
            </p>
            <div className="flex items-center justify-between gap-3 sm:justify-end">
              <button
                type="button"
                disabled={busy || currentPage <= 1}
                onClick={() => search(query, currentPage - 1)}
                className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Previous
              </button>
              <span className="text-xs font-medium text-slate-500">
                Page {totalPages === 0 ? 0 : currentPage} of {totalPages}
              </span>
              <button
                type="button"
                disabled={busy || currentPage >= totalPages}
                onClick={() => search(query, currentPage + 1)}
                className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </section>


      </main>

      <Footer />
    </div>
  );
}
